import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { put } from "redux-saga/effects";
import toast from "react-hot-toast";

import { resolveErrorPolicy } from "../../utils/errorPolicy";
import type { SagaOperationContext } from "../../utils/errorPolicy";
import { SupabaseRequestError } from "../../utils/errors/SupabaseRequestError";
import { reportFromErrorPolicy } from "../../utils/sentryTelemetry/reportFromErrorPolicy";
import {
  getFailureToastMessage,
  getSagaErrorMessage,
  shouldLogSagaErrorToConsole,
  shouldShowFailureToast,
} from "./sagaErrorUi";

/**
 * Low-level saga error handler (Sentry + optional toast + failure action).
 * Always resolves {@link resolveErrorPolicy}; feature code should prefer {@link applySagaError}.
 */
export function* handleSagaError<T>(
  error: unknown,
  policyContext: SagaOperationContext,
  failureAction?: ActionCreatorWithPayload<T>,
  captureInSentry: boolean = true,
  sentryContext?: Record<string, unknown>,
) {
  const errorMessage = getSagaErrorMessage(error);
  const policy = resolveErrorPolicy(error, policyContext);

  if (captureInSentry) {
    const mergedContext: Record<string, unknown> = {
      operationName: policyContext.operationName,
      ...(policyContext.endpoint !== undefined && { endpoint: policyContext.endpoint }),
      policyReason: policy.reason,
      ...(error instanceof SupabaseRequestError &&
        error.requestUrl !== undefined && { supabaseRequestUrl: error.requestUrl }),
      ...sentryContext,
    };

    reportFromErrorPolicy({ error, errorMessage, policy, mergedContext });
  }

  if (shouldLogSagaErrorToConsole(policy, captureInSentry)) {
    console.error("Saga Error:", error);
  }

  if (failureAction) {
    yield put(failureAction(errorMessage as T));
    if (shouldShowFailureToast(policy)) {
      toast.error(getFailureToastMessage(errorMessage));
    }
  }
}
